// ============================================
// DATABASE - Thin wrapper over DataService (Dexie)
// ============================================
// Preserves existing function signatures for backward compatibility.
// All storage now goes through Dexie via DataService.
// Handles one-time migration from old 'quotemaker_db' to new 'quotemaker_v2'.

import { DataService } from './data-service';
import {
    resolveLocalDatabaseIdentity,
    shouldReadLegacyUnscopedDatabase,
} from './local-tenant-database';

let migrationDone = false;

// One-time migration from old raw IndexedDB to new Dexie DB
async function migrateFromOldDB() {
    if (migrationDone) return;
    migrationDone = true;
    try {
        // The raw legacy database predates tenant ownership. Never copy those
        // rows into an authenticated tenant merely because its new namespace
        // is empty. Only the unauthenticated legacy/local-development database
        // may run the historical compatibility migration.
        const localIdentity = resolveLocalDatabaseIdentity();
        if (!shouldReadLegacyUnscopedDatabase(localIdentity)) return;

        // Check if old database exists
        const databases = await indexedDB.databases?.() || [];
        const oldExists = databases.some(d => d.name === 'quotemaker_db');
        if (!oldExists) return;

        // Check if we already migrated (new DB has data)
        const existingProjects = await DataService.projects.getAll().catch(() => []);
        if (existingProjects.length > 0) return; // Already migrated

        console.log('[QM] Migrating data from old quotemaker_db...');
        const oldDb = await new Promise((resolve, reject) => {
            const req = indexedDB.open('quotemaker_db');
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
            setTimeout(() => reject(new Error('Old DB open timeout')), 3000);
        });

        // Migrate each store
        const stores = ['quotes', 'learnings', 'projects', 'business_pricing', 'drafts'];
        for (const storeName of stores) {
            if (!oldDb.objectStoreNames.contains(storeName)) continue;
            try {
                const records = await new Promise((resolve, reject) => {
                    const tx = oldDb.transaction(storeName, 'readonly');
                    const req = tx.objectStore(storeName).getAll();
                    req.onsuccess = () => resolve(req.result || []);
                    req.onerror = () => resolve([]);
                });
                if (records.length > 0) {
                    if (!DataService.db) continue; // API mode - Dexie not exposed
                    const table = DataService.db[storeName];
                    if (table) {
                        await table.bulkPut(records);
                        console.log(`[QM] Migrated ${records.length} records from ${storeName}`);
                    }
                }
            } catch (e) {
                console.warn(`[QM] Failed to migrate ${storeName}:`, e);
            }
        }
        oldDb.close();
        console.log('[QM] Migration complete');
    } catch (e) {
        console.warn('[QM] Old DB migration skipped:', e?.message || e);
    }
}

// Run migration on first access
migrateFromOldDB();

// QUOTES
export async function saveQuote(quote) {
    return DataService.quotes.save(quote);
}

export async function getAllQuotes() {
    return DataService.quotes.getAll();
}

export async function deleteQuoteFromDB(id) {
    return DataService.quotes.delete(id);
}

// DRAFTS
export async function saveDraft(draft) {
    return DataService.drafts.save(draft);
}

export async function getDraft() {
    return DataService.drafts.get();
}

export async function clearDraft() {
    return DataService.drafts.clear();
}

// PROJECTS
export async function saveProject(project) {
    return DataService.projects.save(project);
}

export async function getProject(id) {
    return DataService.projects.getById(id);
}

export async function getAllProjects() {
    return DataService.projects.getAll();
}

export async function deleteProject(id) {
    return DataService.projects.delete(id);
}

export async function getAllProjectSummaries() {
    return DataService.projects.getAllSummaries();
}

export async function cleanupProjectStorage() {
    return DataService.projects.cleanup();
}
